import * as React from "react";
import {
  Box,
  Grid,
  InputLabel,
  Link,
  Radio,
  Select,
  TextField,
} from "@mui/material";
import { PatientType } from "../types";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { ImagesContext } from "../../Context";
import globals from "../../globals/globals";
import moment from "moment";
import InputMask from "react-input-mask";

interface PatientEditProps {
  patient: PatientType | null;
  handlePatientChange: (updated: Partial<PatientType>) => void;
}

const PatientEdit: React.FC<PatientEditProps> = ({
  patient,
  handlePatientChange,
}) => {
  const images = React.useContext(ImagesContext);
  const [openDateOfBirth, setOpenDateOfBirth] = React.useState<boolean>(false);

  const stateOptions = globals.states.map((state) => {
    return (
      <option key={state} value={state}>
        {state}
      </option>
    );
  });

  const handleDateOfBirthChange = (date: moment.Moment | null) => {
    handlePatientChange({
      date_of_birth: date ? date.format("YYYY-MM-DD") : "",
    });
  };

  return (
    <Box className="edit-container">
      <Grid item xs={12} className="claim-edit-container">
        <Grid container>
          <Grid container className="claim-form-container">
            <Grid item xs={2.5} sx={{ mr: 5 }}>
              <Grid container>
                <Grid item xs={12}>
                  <InputLabel htmlFor="last_name" className="field-label">
                    Last Name*
                  </InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <TextField
                    id="last_name"
                    size="small"
                    value={patient?.last_name ?? ""}
                    className="the-field"
                    required
                    autoComplete="new-password"
                    variant="outlined"
                    onChange={(e) =>
                      handlePatientChange({ last_name: e.target.value })
                    }
                  />
                </Grid>
              </Grid>
              <Grid container>
                <Grid item xs={12}>
                  <InputLabel htmlFor="first_name" className="field-label">
                    First Name*
                  </InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <TextField
                    id="first_name"
                    size="small"
                    value={patient?.first_name ?? ""}
                    className="the-field"
                    required
                    autoComplete="new-password"
                    variant="outlined"
                    onChange={(e) =>
                      handlePatientChange({ first_name: e.target.value })
                    }
                  />
                </Grid>
              </Grid>

              <Grid container>
                <Grid item xs={12}>
                  <InputLabel htmlFor="middle_name" className="field-label">
                    Middle Name*
                  </InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <TextField
                    id="middle_name"
                    size="small"
                    autoComplete="off"
                    value={patient?.middle_name ?? ""}
                    className="the-field"
                    required
                    variant="outlined"
                    onChange={(e) =>
                      handlePatientChange({ middle_name: e.target.value })
                    }
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={2.5} sx={{ mr: 5 }}>
              <Grid container>
                <Grid item xs={12}>
                  <InputLabel htmlFor="address" className="field-label">
                    Address Line One*
                  </InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <TextField
                    id="address"
                    size="small"
                    value={patient?.address_line_1 ?? ""}
                    required
                    autoComplete="new-password"
                    variant="outlined"
                    className="the-field"
                    onChange={(e) =>
                      handlePatientChange({ address_line_1: e.target.value })
                    }
                  />
                </Grid>
              </Grid>
              <Grid container>
                <Grid item xs={12}>
                  <InputLabel htmlFor="address" className="field-label">
                    Address Line Two
                  </InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <TextField
                    id="address"
                    size="small"
                    value={patient?.address_line_2 ?? ""}
                    required
                    autoComplete="new-password"
                    variant="outlined"
                    className="the-field"
                    onChange={(e) =>
                      handlePatientChange({ address_line_2: e.target.value })
                    }
                  />
                </Grid>
              </Grid>
              <Grid container>
                <Grid item xs={12}>
                  <InputLabel htmlFor="city" className="field-label">
                    City*
                  </InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <TextField
                    id="city"
                    size="small"
                    value={patient?.city ?? ""}
                    required
                    autoComplete="new-password"
                    variant="outlined"
                    className="the-field"
                    onChange={(e) =>
                      handlePatientChange({ city: e.target.value })
                    }
                  />
                </Grid>
              </Grid>

              <Grid container justifyContent="space-between">
                <Grid container xs={5.5} sx={{ mr: 1 }}>
                  <Grid item xs={12}>
                    <InputLabel htmlFor="zip" className="field-label">
                      ZIP Code*
                    </InputLabel>
                  </Grid>
                  <Grid item xs={12} className="field-container">
                    <TextField
                      id="zip"
                      placeholder="Enter Zip"
                      size="small"
                      value={patient?.zip || ""}
                      required
                      variant="outlined"
                      className="the-field"
                      autoComplete="new-password"
                      onChange={(e) => {
                        const zipCode = e.target.value.replace(/\D/g, "");
                        handlePatientChange({ zip: zipCode });
                      }}
                      slotProps={{
                        htmlInput: {
                          inputMode: "numeric",
                          maxLength: 5,
                        },
                      }}
                    />
                  </Grid>
                </Grid>
                <Grid container xs={5.5} sx={{ mr: 1 }}>
                  <Grid item xs={12}>
                    <InputLabel htmlFor="zipExtension" className="field-label">
                      ZIP Extension
                    </InputLabel>
                  </Grid>
                  <Grid item xs={12} className="field-container">
                    <TextField
                      id="zipExtension"
                      placeholder="Zip Extension"
                      size="small"
                      value={patient?.zipExtension || ""}
                      variant="outlined"
                      className="the-field"
                      autoComplete="off"
                      onChange={(e) => {
                        const zipExtension = e.target.value.replace(/\D/g, "");
                        handlePatientChange({ zipExtension: zipExtension });
                      }}
                      slotProps={{
                        htmlInput: {
                          inputMode: "numeric",
                          maxLength: 4,
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={2.5} sx={{ mr: 5 }}>
              <Grid container>
                <Grid item xs={12}>
                  <InputLabel htmlFor="state" className="field-label">
                    State*
                  </InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <Select
                    id="state"
                    value={patient?.state || ""}
                    required
                    variant="outlined"
                    className="the-field"
                    onChange={(e) =>
                      handlePatientChange({ state: e.target.value })
                    }
                    native
                  >
                    <option value={""}>Select</option>
                    {stateOptions}
                  </Select>
                </Grid>
              </Grid>

              <Box>
                <InputLabel htmlFor="dateOfBirth" className="field-label">
                  Date of Birth*
                </InputLabel>
                <Box
                  className="field-container"
                  sx={{
                    marginLeft: "0px",
                    backgroundColor: "#efe9e7",
                    display: "flex",
                    borderRadius: "5px",
                    paddingRight: "10.5px",
                  }}
                >
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <Box
                      className="date-box"
                      sx={{
                        width: "88%",
                        maxHeight: 41.13,
                      }}
                    >
                      <DemoContainer components={["DatePicker", "DatePicker"]}>
                        <DatePicker
                          open={openDateOfBirth}
                          onOpen={() => setOpenDateOfBirth(true)}
                          onClose={() => setOpenDateOfBirth(false)}
                          value={
                            patient.date_of_birth
                              ? moment(patient.date_of_birth)
                              : null
                          }
                          onChange={(x) => {
                            handleDateOfBirthChange(x);
                          }}
                          format="MM/DD/YYYY"
                          disableOpenPicker
                          disabled={false}
                          slotProps={{
                            textField: {
                              placeholder: "Date of Birth",
                            },
                            openPickerIcon: images.calendar_icon,
                          }}
                          className="report-date-input no-border-outline"
                          maxDate={null}
                        />
                      </DemoContainer>
                    </Box>
                  </LocalizationProvider>
                  <Link
                    className="invite-app-user-link"
                    onClick={() => setOpenDateOfBirth(true)}
                  >
                    <img
                      src={images.calendar_icon}
                      alt="Calendar"
                      className="action-icon-image"
                      style={{ width: 22.75, maxHeight: 26 }}
                    />
                  </Link>
                </Box>
              </Box>

              <Grid container>
                <Grid item xs={12}>
                  <InputLabel
                    htmlFor="mobile_phone_number"
                    className="field-label"
                  >
                    Phone Number*
                  </InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <InputMask
                    id="mobile-phone-number"
                    mask="999-999-9999"
                    value={patient?.mobile_phone_number}
                    disabled={false}
                    maskChar=""
                    variant="outlined"
                    className="the-field"
                    size="small"
                    onChange={(e) =>
                      handlePatientChange({
                        mobile_phone_number: e.target.value,
                      })
                    }
                  >
                    {() => (
                      <TextField
                        variant="outlined"
                        size="small"
                        className="the-field"
                        autoComplete="off"
                      />
                    )}
                  </InputMask>
                </Grid>
              </Grid>

              <Grid container className="field-container">
                <Grid item xs={12}>
                  <InputLabel htmlFor="gender" className="field-label">
                    Sex*
                  </InputLabel>
                </Grid>
                <Grid item xs={12}>
                  <div className="row" style={{ height: 40 }}>
                    <Radio
                      checked={patient?.gender === "Male"}
                      onChange={(e) => handlePatientChange({ gender: "Male" })}
                      value="Male"
                      name="radio-buttons"
                      inputProps={{ "aria-label": "Male" }}
                      style={{ backgroundColor: "transparent" }}
                      sx={{
                        color: "#A29D9B",
                        ml: 1,
                        width: 32,
                        height: 32,
                      }}
                    />
                    <p className="radio-label">Male</p>
                    <Radio
                      checked={patient?.gender === "Female"}
                      onChange={(e) =>
                        handlePatientChange({ gender: "Female" })
                      }
                      value="Female"
                      name="radio-buttons"
                      inputProps={{ "aria-label": "Female" }}
                      style={{ backgroundColor: "transparent" }}
                      sx={{
                        color: "#A29D9B",
                        ml: 2,
                        width: 32,
                        height: 32,
                      }}
                    />
                    <p className="radio-label">Female</p>
                    <Radio
                      checked={patient?.gender === "Unknown"}
                      onChange={(e) =>
                        handlePatientChange({ gender: "Unknown" })
                      }
                      value="Unknown"
                      name="radio-buttons"
                      inputProps={{ "aria-label": "Unknown" }}
                      style={{ backgroundColor: "transparent" }}
                      sx={{
                        color: "#A29D9B",
                        ml: 2,
                        width: 32,
                        height: 32,
                      }}
                    />
                    <p className="radio-label">Unknown</p>
                  </div>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PatientEdit;
